import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, Loader2, Trash2, Upload, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import { DatePicker } from '@/shared/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { cajaBase } from '@/shared/lib/cajasEfectivo';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { MedioDePagoItem } from '@/shared/api/controllers/mediosDePago';
import type { ComprobantePagoItem } from '@/shared/api/controllers/comprobantePagos';
import type { Moneda } from './MoneyCell';
import { MoneyInput } from './MoneyInput';
import { FiscalDataMissingWarning } from './FiscalDataMissingWarning';

/**
 * "Subir Comprobante de Pago" dialog — opened from the $ icon on a
 * `Pendiente de Cobro` row in Carga de Pagos. Mirrors PowerApps's
 * `PopUp_CargarComprobanteCobro` (Screen_Pagos.pa.yaml). Re-asks the
 * user for Fecha de pago, Método, Monto, and Lleva Factura, plus a
 * required comprobante file. On Guardar the parent triggers a
 * two-step commit: PATCH the comprobante's form fields, then upload
 * the file (the upload's promesa-hook does the Status_P + Status_RO
 * + stock consume).
 */
export type SubirComprobanteFormValue = {
  medioPagoId: string;
  medioPago: string;
  /** dd/mm/yyyy */
  fechaPago: string;
  monto: number;
  contieneFactura: boolean;
  file: File;
};

const ALLOWED_MIMES = ['application/pdf', 'image/png', 'image/jpeg'];
const MAX_BYTES = 4 * 1024 * 1024;

export function SubirComprobanteDialog({
  open,
  comp,
  ventaMoneda,
  mediosDePago,
  disponibleAFacturar,
  disponibleSinFactura,
  isExento = false,
  clienteFiscalMissing = null,
  loading = false,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  comp: ComprobantePagoItem;
  ventaMoneda: Moneda;
  mediosDePago: MedioDePagoItem[];
  /** Remaining-to-invoice for the venta in venta currency. The dialog
   *  adds back the comp's own factura contribution when computing the
   *  cap, so toggling its existing SI back on doesn't trip the check. */
  disponibleAFacturar: number;
  /** Cupo sin factura de la venta SIN contar esta fila (el padre ya le
   *  devuelve lo suyo), en moneda de la venta. En 0 —venta al 100%— el pago
   *  lleva factura sí o sí. */
  disponibleSinFactura: number;
  /** Client is IVA Exento — the cap equals the venta total, so an over-limit
   *  means "exceeds the venta total"; show the generic "Monto excedido". */
  isExento?: boolean;
  /** AR only: receptor fiscal fields (condición IVA / CUIT) the vendedor still
   *  has to complete before this client can be facturado. Non-null → a warning
   *  shows when "Lleva Factura" is on. */
  clienteFiscalMissing?: string[] | null;
  loading?: boolean;
  onSubmit: (value: SubirComprobanteFormValue) => void;
  onCancel: () => void;
}) {
  const f = comp.fields;
  // "Efectivo Pesos N" se muestra como su base: la gemela no está en el
  // selector y el server la vuelve a resolver con Lleva Factura al guardar.
  const initialMedio =
    mediosDePago.find((m) => m.fields.Medio_MDP === cajaBase(f.MedioPago_CP ?? '')) ?? null;

  const [medio, setMedio] = useState<MedioDePagoItem | null>(initialMedio);
  // Local medios are denominated in the active country's home currency
  // (ARS / MXN). Drives the Monto MoneyInput's currency formatting.
  const { config: activeCountry } = useActiveCountry();
  const homeCurrency = activeCountry.currency.primary;
  const [fechaPago, setFechaPago] = useState(arToIso(f.FechaPago_CP) || todayIso());
  const [monto, setMonto] = useState(String(f.Monto_CP ?? ''));
  const [contieneFactura, setContieneFactura] = useState(f.ContieneFactura_CP === 'SI');
  // Facturación Previa: si la factura YA se emitió, el server congela los
  // campos fiscales de esta pantalla (el CAE salió por ese monto y con esa
  // condición). Los bloqueamos acá también para no ofrecer una edición que se
  // descarta en silencio. El comprobante se sigue pudiendo subir: es el avance
  // normal de la promesa cuando entra la plata.
  const facturaEmitida = f.Facturado_CP === 'SI';
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset on each (re-)open so reopening always reflects the current
  // server-side row state, not whatever the previous session left.
  useEffect(() => {
    if (!open) return;
    const next =
      mediosDePago.find((m) => m.fields.Medio_MDP === cajaBase(f.MedioPago_CP ?? '')) ?? null;
    setMedio(next);
    setFechaPago(arToIso(f.FechaPago_CP) || todayIso());
    setMonto(String(f.Monto_CP ?? ''));
    setContieneFactura(f.ContieneFactura_CP === 'SI');
    setFile(null);
    setFileError(null);
  }, [open, comp.id, mediosDePago, f.MedioPago_CP, f.FechaPago_CP, f.Monto_CP, f.ContieneFactura_CP]);

  const montoNum = parseMoney(monto) ?? NaN;

  // Sin cupo sin factura (venta al 100%) el pago lleva factura: el toggle
  // queda tildado y trabado. Si no, al subir el comprobante de una promesa
  // se podía destildar y el efectivo caía en la caja N. El server rechaza
  // igual el 100%. Con la factura ya emitida los campos están congelados.
  const ventaExigeFactura = !facturaEmitida && disponibleSinFactura <= 0.005;
  const llevaFactura = contieneFactura || ventaExigeFactura;

  // Monto en moneda de la venta, con el mismo recálculo que hace el server
  // al guardar (Pesos_CP = monto × TC o monto / TC si la promesa era cruzada).
  const montoEnVenta = useMemo(() => {
    if (!Number.isFinite(montoNum)) return NaN;
    const oldPesos = parseMoney(f.Pesos_CP) ?? 0;
    const oldTC = parseMoney(f.TipoCambio_CP) ?? 0;
    const oldMonto = parseMoney(f.Monto_CP) ?? 0;
    if (oldPesos > 0 && oldTC > 0 && oldMonto > 0) {
      return oldPesos > oldMonto ? montoNum * oldTC : montoNum / oldTC;
    }
    return montoNum;
  }, [montoNum, f.Pesos_CP, f.TipoCambio_CP, f.Monto_CP]);
  const excedidoSinFactura =
    !facturaEmitida &&
    !llevaFactura &&
    Number.isFinite(montoEnVenta) &&
    montoEnVenta > disponibleSinFactura + 0.005;

  const canSubmit =
    medio !== null &&
    Number.isFinite(montoNum) &&
    montoNum > 0 &&
    Boolean(fechaPago) &&
    file !== null &&
    !excedidoSinFactura;

  // Fiscal gate: can't save a comprobante that lleva factura while the client's
  // fiscal data is incomplete (Facturación couldn't emit its CAE). Escapable by
  // unchecking Lleva Factura.
  const fiscalBlocked = llevaFactura && (clienteFiscalMissing?.length ?? 0) > 0;

  // Lleva Factura guard. The parent's `disponibleAFacturar` already
  // accounts for this comp's existing factura contribution being
  // subtracted (when `f.ContieneFactura_CP === 'SI'`), so we add it
  // back here — otherwise toggling the existing SI off and on would
  // wrongly trip the warning.
  const ownExistingFacturaContribution = useMemo(() => {
    if (f.ContieneFactura_CP !== 'SI') return 0;
    const isUsdMedio =
      cajaBase(f.MedioPago_CP ?? '') === 'Efectivo Dolar' ||
      f.MedioPago_CP === 'Transferencia Galicia Dolar' ||
      f.MedioPago_CP === 'Transferencia Dolar';
    return (ventaMoneda === 'ARS' && isUsdMedio) ||
      (ventaMoneda === 'USD' && !isUsdMedio)
      ? parseMoney(f.Pesos_CP) ?? parseMoney(f.Monto_CP) ?? 0
      : parseMoney(f.Monto_CP) ?? 0;
  }, [f.ContieneFactura_CP, f.MedioPago_CP, f.Pesos_CP, f.Monto_CP, ventaMoneda]);

  const remainingFactura = Math.max(
    0,
    disponibleAFacturar + ownExistingFacturaContribution,
  );
  const [excedidoOpen, setExcedidoOpen] = useState(false);
  const [excedidoIntento, setExcedidoIntento] = useState(0);

  const handleFacturaToggle = (next: boolean) => {
    if (!next) {
      setContieneFactura(false);
      return;
    }
    if (Number.isFinite(montoNum) && montoNum > remainingFactura + 0.005) {
      setExcedidoIntento(montoNum);
      setExcedidoOpen(true);
      setContieneFactura(false);
      return;
    }
    setContieneFactura(true);
  };

  const triggerFilePick = () => {
    if (loading) return;
    setFileError(null);
    fileInputRef.current?.click();
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!picked) return;
    if (!ALLOWED_MIMES.includes(picked.type)) {
      setFileError('Tipo no soportado: solo PDF, PNG o JPG.');
      return;
    }
    if (picked.size > MAX_BYTES) {
      setFileError('El archivo supera los 4 MB.');
      return;
    }
    setFile(picked);
  };

  const handleSubmit = () => {
    if (!medio || !canSubmit || !file || fiscalBlocked) return;
    onSubmit({
      medioPagoId: medio.id,
      medioPago: medio.fields.Medio_MDP?.trim() || '',
      fechaPago: isoToAr(fechaPago),
      monto: montoNum,
      contieneFactura: llevaFactura,
      file,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !loading && onCancel()}>
      <DialogContent
        hideClose={loading}
        className="max-w-xl"
        processing={loading && 'Subiendo comprobante…'}
      >
        <DialogHeader>
          <DialogTitle>Subir Comprobante de Pago</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1">
            <Label className="text-sm">Fecha de pago</Label>
            <DatePicker
              value={fechaPago}
              onChange={setFechaPago}
              ariaLabel="Fecha de pago"
              clearable={false}
            />
          </div>
          <div className="grid grid-cols-[1fr,1fr,auto] items-end gap-3">
            <div className="space-y-1">
              <Label className="text-sm">Método</Label>
              <Combobox
                value={medio}
                onChange={setMedio}
                items={mediosDePago}
                getKey={(m) => m.id}
                getLabel={(m) => m.fields.Medio_MDP?.trim() || '—'}
                getSublabel={(m) => (m.fields.Categoria_MDP === 'Internacional' ? 'USD' : homeCurrency)}
                matcher={(m, q) =>
                  (m.fields.Medio_MDP ?? '').toLowerCase().includes(q) ||
                  (m.fields.Categoria_MDP === 'Internacional' ? 'usd' : homeCurrency.toLowerCase()).includes(q)
                }
                placeholder="Seleccionar método"
                disabled={loading || facturaEmitida}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Monto</Label>
              <MoneyInput
                variant="filled"
                value={monto}
                moneda={
                  medio?.fields.Categoria_MDP === 'Internacional' ? 'USD' : homeCurrency
                }
                onChange={setMonto}
                disabled={loading || facturaEmitida}
                className="h-10 text-right"
              />
            </div>
            <CheckboxRow
              checked={llevaFactura}
              onChange={handleFacturaToggle}
              label="Lleva Factura"
              disabled={loading || facturaEmitida || ventaExigeFactura}
              title={
                ventaExigeFactura
                  ? 'Esta venta no tiene saldo para cobrar sin factura'
                  : undefined
              }
            />
          </div>
          {ventaExigeFactura && (
            <p className="text-xs text-muted-foreground">
              Esta venta no tiene saldo para cobrar sin factura, así que el pago lleva
              factura.
            </p>
          )}
          {excedidoSinFactura && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <p className="font-medium">Monto excedido sin factura</p>
              <p className="mt-0.5 leading-snug">
                {`El monto supera lo que esta venta admite cobrar sin factura (${formatMoney(disponibleSinFactura, ventaMoneda)}). Reducí el monto o marcá Lleva Factura.`}
              </p>
            </div>
          )}
          {facturaEmitida && (
            <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
              Este cobro ya tiene factura emitida
              {f.NroFactura_CP ? ` (${f.NroFactura_CP})` : ''}. Podés subir el
              comprobante de pago y ajustar la fecha, pero el método, el monto y
              la condición de factura quedan fijos: sólo una Nota de Crédito
              cambia algo ya autorizado ante ARCA.
            </p>
          )}
          {llevaFactura && <FiscalDataMissingWarning missing={clienteFiscalMissing} />}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={triggerFilePick}
                disabled={loading}
                className="flex-1 justify-start gap-2"
              >
                <Upload className="h-4 w-4 shrink-0" aria-hidden />
                <span className="truncate">{file ? file.name : 'Subir comprobante'}</span>
              </Button>
              {file && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setPreview(file)}
                    disabled={loading}
                    aria-label="Ver comprobante"
                    title="Ver comprobante"
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setFile(null)}
                    disabled={loading}
                    className="text-muted-foreground hover:text-destructive"
                    aria-label="Quitar comprobante"
                    title="Quitar comprobante"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                hidden
                onChange={onFileInputChange}
              />
            </div>
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || loading || fiscalBlocked}
            className="gap-2"
          >
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
      {preview && <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />}
      <FacturaExcedidaDialog
        open={excedidoOpen}
        intento={excedidoIntento}
        disponible={remainingFactura}
        moneda={ventaMoneda}
        isExento={isExento}
        onClose={() => setExcedidoOpen(false)}
      />
    </Dialog>
  );
}

/**
 * Warning shown when the user toggles "Lleva Factura" on but the monto
 * exceeds the remaining-to-invoice. Mirrors PowerApps'
 * `PopUp_AdvertenciaPromeMontoDisponible`.
 */
function FacturaExcedidaDialog({
  open,
  intento,
  disponible,
  moneda,
  isExento = false,
  onClose,
}: {
  open: boolean;
  intento: number;
  disponible: number;
  moneda: Moneda;
  isExento?: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isExento ? 'Monto excedido' : 'Monto de factura excedido'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3 text-sm">
          <p>
            {isExento ? (
              'El monto del pago supera el total de la venta. Reducí el monto antes de continuar.'
            ) : (
              <>
                El monto del pago supera lo disponible para facturar. Reducí el
                monto o desmarcá{' '}
                <span className="font-medium">Lleva Factura</span> antes de
                continuar.
              </>
            )}
          </p>
          <div className="grid grid-cols-2 gap-2 rounded-lg border bg-secondary px-3 py-2">
            <span className="text-muted-foreground">Monto cargado</span>
            <span className="text-right font-medium text-foreground">
              {formatMoney(intento, moneda)}
            </span>
            <span className="text-muted-foreground">
              {isExento ? 'Disponible de la venta' : 'Disponible a facturar'}
            </span>
            <span className="text-right font-medium text-foreground">
              {formatMoney(disponible, moneda)}
            </span>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Entendido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilePreviewDialog({ file, onClose }: { file: File; onClose: () => void }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const isImage = file.type.startsWith('image/');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate">{file.name}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {!blobUrl ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : isImage ? (
            <img
              src={blobUrl}
              alt={file.name}
              className="mx-auto max-h-[70vh] w-auto rounded-lg"
            />
          ) : (
            <iframe
              src={blobUrl}
              title={file.name}
              className="h-[70vh] w-full rounded-lg border"
            />
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} className="gap-2">
            <X className="h-4 w-4" aria-hidden />
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CheckboxRow({
  checked,
  onChange,
  label,
  disabled,
  title,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  /** Native tooltip — explains why a locked toggle can't be unchecked. */
  title?: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      title={title}
      className={cn(
        'inline-flex items-center gap-2 pb-2.5 text-sm transition-colors disabled:opacity-50',
        checked ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'flex h-5 w-5 items-center justify-center rounded border',
          checked ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-border bg-background',
        )}
      >
        {checked && (
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </span>
      {label}
    </button>
  );
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isoToAr(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function arToIso(ar: string | null | undefined): string {
  if (!ar) return '';
  const [d, m, y] = ar.split('/');
  if (!d || !m || !y) return '';
  return `${y}-${m}-${d}`;
}
